/**
 * Halaman Detail Instruksi
 * Menampilkan detail lengkap instruksi/tugas
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import Icon from "@mui/material/Icon";
import SvgIcon from "@mui/material/SvgIcon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import Avatar from "@mui/material/Avatar";
import MDDatePicker from "components/MDDatePicker";
import MDDropzone from "components/MDDropzone";
import MDInput from "components/MDInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD, API_CHAT } from "utils";
import { logoutUser } from "actions/AuthAction";
import { getValidAccessToken } from "utils/tokenUtils";
import { forceLogoutInactive } from "utils/forceLogoutInactive";
import CircularProgress from "@mui/material/CircularProgress";
import swal from "sweetalert";
import ChatIcon from "@mui/icons-material/Chat";

// Ikon WhatsApp (SVG)
const WhatsAppIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
    />
  </SvgIcon>
);

// Format nomor HP untuk WhatsApp (62xxxxxxxxxx)
const formatNoHPForWhatsApp = (noHP) => {
  if (!noHP || typeof noHP !== "string") return null;
  let num = noHP.replace(/\D/g, "");
  if (num.startsWith("0")) num = "62" + num.slice(1);
  else if (!num.startsWith("62")) num = "62" + num;
  return num.length >= 10 ? num : null;
};

// Buat URL WhatsApp dengan pesan instruksi
const buildWhatsAppUrl = (noHP, instruksiItem) => {
  const num = formatNoHPForWhatsApp(noHP);
  if (!num) return null;
  const baseUrl = window.location.origin;
  const detailLink = `${baseUrl}/#/task-management/tugas/instruksi-detail/${instruksiItem?.instruksi_id || ""}`;
  const perihal = instruksiItem?.perihal || "Instruksi";
  let isiInstruksi =
    instruksiItem?.isi_instruksi?.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || "-";
  if (isiInstruksi.length > 800) {
    isiInstruksi = isiInstruksi.slice(0, 800) + "...";
  }
  const pemberiInstruksi = instruksiItem?.pengirim?.nama || "-";
  const deadline = instruksiItem?.tanggal_selesai
    ? (() => {
        const d = new Date(instruksiItem.tanggal_selesai);
        return isNaN(d) ? "-" : d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
      })()
    : "-";
  const message = [
    `*INSTRUKSI*`,
    ``,
    `*Perihal:* ${perihal}`,
    ``,
    `*Isi Instruksi:*`,
    isiInstruksi,
    ``,
    `*Pemberi Instruksi:* ${pemberiInstruksi}`,
    `*Deadline:* ${deadline}`,
    ``,
    `Silakan buka link berikut untuk detail lengkap:`,
    detailLink,
    `\n_Pesan ini dikirimkan dari Esa Office App_`,
  ]
    .filter(Boolean)
    .join("\n");
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
};

const PRIORITAS_COLOR = {
  Rendah: "#4caf50",
  Sedang: "#ffb300",
  Tinggi: "#f44336",
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Memproses HTML agar link clickable dan terbuka di tab baru.
 * - Menambahkan target="_blank" rel="noopener noreferrer" pada tag <a>
 * - Mengonversi URL plain text (di luar tag HTML) menjadi link
 */
const processHtmlWithClickableLinks = (html) => {
  if (!html || typeof html !== "string") return "";
  let result = html;
  // 1. Tambah target="_blank" pada tag <a> yang belum punya
  result = result.replace(
    /<a\s+([^>]*?)>/gi,
    (match, attrs) => {
      if (attrs.includes("target=")) return match;
      return `<a ${attrs.trim()} target="_blank" rel="noopener noreferrer">`;
    }
  );
  // 2. Konversi URL plain text menjadi link - hanya di text node di luar tag <a>
  const parts = result.split(/(<[^>]+>)/g);
  let insideAnchor = false;
  result = parts
    .map((part) => {
      if (part.startsWith("<") && part.endsWith(">")) {
        if (/^<\/a\s*>/i.test(part)) insideAnchor = false;
        else if (/^<a[\s>]/i.test(part)) insideAnchor = true;
        return part;
      }
      if (insideAnchor) return part;
      const urlRegex = /(https?:\/\/[^\s<>"]+)|(www\.[^\s<>"]+)/gi;
      return part.replace(urlRegex, (url) => {
        const href = url.match(/^www\./i) ? `https://${url}` : url;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#1976d2;text-decoration:underline">${url}</a>`;
      });
    })
    .join("");
  return result;
};

const isValidFileType = (file) => {
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "image/jpeg",
    "image/jpg",
  ];
  const allowedExtensions = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".xlsm", ".jpg", ".jpeg"];
  const ext = file.name?.toLowerCase().substring(file.name.lastIndexOf("."));
  return allowedTypes.includes(file.type) || allowedExtensions.includes(ext);
};

function InstruksiDetail() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { instruksiId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [instruksi, setInstruksi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [isPemberi, setIsPemberi] = useState(false);
  const [isPenerima, setIsPenerima] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  // State untuk tindak lanjut (hanya untuk penerima)
  const [openPilihanDialog, setOpenPilihanDialog] = useState(false);
  const [openTindakLanjutDialog, setOpenTindakLanjutDialog] = useState(false);
  const [openModalLanjutkan, setOpenModalLanjutkan] = useState(false);
  const [tanggalTL, setTanggalTL] = useState(new Date());
  const [keteranganTL, setKeteranganTL] = useState("");
  const [filesTL, setFilesTL] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [penerimaOptions, setPenerimaOptions] = useState([]);
  const [penerima, setPenerima] = useState({ penerima: [] });
  const [modalValues, setModalValues] = useState({
    catatan: "",
    files: [],
    penerima: [],
    status_pelaksana: "",
    waktu_diterima: "",
    waktu_selesai: "",
  });
  const dropzoneRefTL = useRef(null);
  const dropzoneRefLanjutkan = useRef(null);

  // State untuk respon pemberi instruksi (saat instruksi selesai dilaksanakan)
  const [openResponDialog, setOpenResponDialog] = useState(false);
  const [selectedPenerimaForRespon, setSelectedPenerimaForRespon] = useState(null);
  const [selectedRespon, setSelectedRespon] = useState("");
  const [openRatingDialog, setOpenRatingDialog] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [submittingRespon, setSubmittingRespon] = useState(false);

  // State untuk chat (per penerima card)
  const [chatMessagesByPenerima, setChatMessagesByPenerima] = useState({});
  const [chatMessages, setChatMessages] = useState([]); // untuk dialog
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [selectedChatPenerima, setSelectedChatPenerima] = useState(null);
  const [selectedChatInstruksiPenerimaId, setSelectedChatInstruksiPenerimaId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
    } else {
      logoutUser(navigate);
    }
  }, [navigate]);

  const loadData = async () => {
    if (!userId || !instruksiId) return;
    try {
      setLoading(true);
      setUnauthorized(false);
      const res = await axios.get(
        `${API_ESA_OFFICE}instruksi/detail/${instruksiId}/${userId}`
      );
      const data = res.data;
      setInstruksi(data);
      setIsPemberi(!!data.is_pemberi);
      setIsPenerima(!!data.is_penerima);
    } catch (err) {
      if (err.response?.status === 403) {
        setUnauthorized(true);
        setInstruksi(null);
      } else if (err.response?.status === 404) {
        setInstruksi(null);
      } else {
        setInstruksi(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [instruksiId, userId]);

  // ----- CHAT -----
  const getFreshToken = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    if (!user?.uid_RSS) {
      throw new Error("User uid_RSS tidak ditemukan");
    }
    try {
      const tokenResponse = await axios.get(
        `${API_ESA_OFFICE}users/uid/${user.uid_RSS}`,
        { withCredentials: true }
      );
      if (tokenResponse.data?.accessToken) {
        const token = tokenResponse.data.accessToken;
        const updatedUser = { ...user, accessToken: token };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return token;
      }
      throw new Error("Access token tidak ditemukan");
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.status_EO != null) {
        forceLogoutInactive();
        return;
      }
      throw error;
    }
  }, []);

  const fetchChatHistory = useCallback(
    async (instruksiItem, token, instruksiPenerimaId = null, markAsRead = false) => {
      const id = instruksiItem?.instruksi_id;
      if (!id) return null;
      const url = instruksiPenerimaId
        ? `${API_CHAT}instruksi/${id}?instruksi_penerima_id=${instruksiPenerimaId}`
        : `${API_CHAT}instruksi/${id}`;
      let response;
      try {
        response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      } catch (error) {
        if (error.response?.status === 403) {
          try {
            const user = JSON.parse(localStorage.getItem("user")) || {};
            token = await getFreshToken(user);
            response = await axios.get(url, {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true,
            });
          } catch (retryError) {
            throw retryError;
          }
        } else throw error;
      }
      if (response?.data?.success) {
        const messages = response.data.data || [];
        const key = instruksiPenerimaId || "all";
        const user = JSON.parse(localStorage.getItem("user")) || {};
        const currentUserId = user.user_id || user.uid_RSS || user.uid;
        let updatedMessages = messages;
        if (markAsRead) {
          try {
            const readUrl = instruksiPenerimaId
              ? `${API_CHAT}instruksi/${id}/read-all?instruksi_penerima_id=${instruksiPenerimaId}`
              : `${API_CHAT}instruksi/${id}/read-all`;
            await axios.put(readUrl, {}, {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true,
            });
            updatedMessages = messages.map((msg) => {
              if (!msg.waktu_baca && msg.penerima_id == currentUserId) {
                return { ...msg, waktu_baca: new Date().toISOString() };
              }
              return msg;
            });
          } catch (e) {}
        }
        setChatMessagesByPenerima((prev) => ({ ...prev, [key]: updatedMessages }));
        return updatedMessages;
      }
      return [];
    },
    [getFreshToken]
  );

  // Fetch chat untuk setiap penerima saat instruksi load
  useEffect(() => {
    if (!instruksi?.instruksi_id || !userId) return;
    const user = JSON.parse(localStorage.getItem("user")) || {};
    if (!user?.uid_RSS) return;
    const penerimaList = instruksi.rincian_penerima || [];
    if (penerimaList.length === 0) return;
    getFreshToken().then((token) => {
      if (!token) return;
      penerimaList.forEach((p) => {
        if (p.id) fetchChatHistory(instruksi, token, p.id);
      });
    }).catch(() => {});
  }, [instruksi?.instruksi_id, instruksi?.rincian_penerima, userId, getFreshToken, fetchChatHistory]);

  // Polling chat saat user kembali ke tab (visibilitychange)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!instruksi?.instruksi_id || !userId) return;
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (!user?.uid_RSS) return;
      const penerimaList = instruksi.rincian_penerima || [];
      if (penerimaList.length === 0) return;
      getFreshToken().then((token) => {
        if (!token) return;
        penerimaList.forEach((p) => {
          if (p.id) fetchChatHistory(instruksi, token, p.id);
        });
        // Jika dialog chat terbuka, refresh chat yang aktif
        if (openChatDialog && (selectedChatInstruksiPenerimaId ?? selectedChatPenerima?.instruksi_penerima_id)) {
          const ipid = selectedChatInstruksiPenerimaId ?? selectedChatPenerima?.instruksi_penerima_id;
          fetchChatHistory(instruksi, token, ipid, true).then((messages) => {
            setChatMessages(messages || []);
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
          });
        }
      }).catch(() => {});
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [instruksi?.instruksi_id, instruksi?.rincian_penerima, openChatDialog, selectedChatInstruksiPenerimaId, selectedChatPenerima, userId, getFreshToken, fetchChatHistory]);

  const formatChatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    const h = date.getHours().toString().padStart(2, "0");
    const m = date.getMinutes().toString().padStart(2, "0");
    const timeStr = `${h}:${m}`;
    if (diffDays === 0) return timeStr;
    if (diffDays === 1) return `Kemarin ${timeStr}`;
    if (diffDays < 7) {
      const days = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
      return `${days[date.getDay()]} ${timeStr}`;
    }
    const months = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
    if (date.getFullYear() !== now.getFullYear()) {
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()} ${timeStr}`;
    }
    return `${date.getDate()} ${months[date.getMonth()]} ${timeStr}`;
  };

  // Chat partner untuk setiap penerima: penerima chat dengan pengirim, pemberi chat dengan penerima
  const getChatPartnerForPenerima = useCallback((penerima) => {
    if (!instruksi || !penerima?.id) return null;
    if (isPenerima) {
      const p = instruksi.pengirim;
      if (!p?.user_id && !p?.uid_RSS) return null;
      return {
        user_id: p.user_id || p.uid_RSS,
        penerima_id: p.user_id || p.uid_RSS,
        nama: p.nama || "Pengirim",
        uid_RSS: p.uid_RSS,
        instruksi_penerima_id: penerima.id,
      };
    }
    const uid = penerima.penerima_id || penerima.user_id || penerima.uid_RSS;
    if (!uid) return null;
    return {
      user_id: uid,
      penerima_id: uid,
      nama: penerima.nama || "Penerima",
      uid_RSS: penerima.uid_RSS,
      instruksi_penerima_id: penerima.id,
    };
  }, [instruksi, isPenerima]);

  const handleOpenChat = useCallback(async (penerima) => {
    const partner = getChatPartnerForPenerima(penerima);
    if (!instruksi || !partner) return;
    setSelectedChatPenerima(partner);
    setSelectedChatInstruksiPenerimaId(penerima.id);
    setOpenChatDialog(true);
    setLoadingChat(true);
    setChatMessages(chatMessagesByPenerima[penerima.id] || []);
    setNewMessage("");
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (!user?.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        return;
      }
      const token = await getFreshToken(user);
      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        return;
      }
      const messages = await fetchChatHistory(instruksi, token, penerima.id, true);
      setChatMessages(messages || []);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (e) {
      console.error(e);
      swal("Error", "Gagal memuat chat.", "error");
      setChatMessages([]);
    } finally {
      setLoadingChat(false);
    }
  }, [instruksi, getChatPartnerForPenerima, getFreshToken, fetchChatHistory, chatMessagesByPenerima]);

  const handleCloseChat = useCallback(() => {
    setOpenChatDialog(false);
    setSelectedChatPenerima(null);
    setSelectedChatInstruksiPenerimaId(null);
    setChatMessages([]);
    setNewMessage("");
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !instruksi || !selectedChatPenerima) return;
    const text = newMessage.trim();
    setNewMessage("");
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const currentUserId = user.user_id || user.uid_RSS || user.uid;
    const tempId = `temp-${Date.now()}`;
    const tempMsg = {
      id_chat: tempId,
      pesan: text,
      pengirim_id: currentUserId,
      pengirim_nama: user.nama || "Anda",
      pengirim_profil: user.uid_RSS,
      waktu_kirim: new Date().toISOString(),
      isSending: true,
    };
    setChatMessages((prev) => (prev.some((m) => m.id_chat === tempId) ? prev : [...prev, tempMsg]));
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    try {
      if (!user?.uid_RSS) {
        swal("Error", "Data user tidak lengkap.", "error");
        setNewMessage(text);
        return;
      }
      const token = await getFreshToken(user);
      if (!token) {
        swal("Error", "Token tidak ditemukan.", "error");
        setNewMessage(text);
        return;
      }
      await axios.post(
        `${API_CHAT}instruksi`,
        {
          instruksi_id: parseInt(instruksi.instruksi_id),
          instruksi_penerima_id: selectedChatInstruksiPenerimaId || selectedChatPenerima?.instruksi_penerima_id || null,
          penerima_id: selectedChatPenerima.user_id || selectedChatPenerima.penerima_id,
          pesan: text,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      setChatMessages((prev) =>
        prev.map((m) => (m.id_chat === tempId ? { ...m, isSending: false } : m))
      );
      const ipid = selectedChatInstruksiPenerimaId ?? selectedChatPenerima?.instruksi_penerima_id;
      const messages = await fetchChatHistory(instruksi, token, ipid, true);
      setChatMessages(messages || []);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (e) {
      console.error(e);
      swal("Error", "Gagal mengirim pesan.", "error");
      setNewMessage(text);
      setChatMessages((prev) => prev.filter((m) => m.id_chat !== tempId));
    }
  }, [newMessage, instruksi, selectedChatPenerima, selectedChatInstruksiPenerimaId, getFreshToken, fetchChatHistory]);

  const handleRefreshChat = useCallback(async () => {
    if (!instruksi || refreshingChat) return;
    const ipid = selectedChatInstruksiPenerimaId ?? selectedChatPenerima?.instruksi_penerima_id;
    setRefreshingChat(true);
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const token = await getFreshToken(user);
      const messages = await fetchChatHistory(instruksi, token, ipid, true);
      setChatMessages(messages || []);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (e) {
      swal("Error", "Gagal refresh chat.", "error");
    } finally {
      setRefreshingChat(false);
    }
  }, [instruksi, refreshingChat, selectedChatInstruksiPenerimaId, selectedChatPenerima, getFreshToken, fetchChatHistory]);

  const handleDeleteMessage = useCallback(
    async (chatId) => {
      if (!chatId || String(chatId).startsWith("temp-")) return;

      const confirmed = await swal({
        title: "Hapus Pesan?",
        text: "Pesan yang dihapus tidak dapat dikembalikan.",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Batal",
            value: false,
            visible: true,
          },
          confirm: {
            text: "Hapus",
            value: true,
            visible: true,
          },
        },
        dangerMode: true,
      });

      if (!confirmed) return;

      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        const token = await getFreshToken(user);

        await axios.delete(`${API_CHAT}instruksi/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        setChatMessages((prev) => prev.filter((m) => m.id_chat !== chatId));
        const key = selectedChatInstruksiPenerimaId ?? selectedChatPenerima?.instruksi_penerima_id;
        if (key != null) {
          setChatMessagesByPenerima((prev) => ({
            ...prev,
            [key]: (prev[key] || []).filter((m) => m.id_chat !== chatId),
          }));
        }
        swal("Berhasil", "Pesan berhasil dihapus", "success");
      } catch (e) {
        console.error("Error deleting message:", e);
        swal("Error", "Gagal menghapus pesan", "error");
      }
    },
    [getFreshToken, selectedChatInstruksiPenerimaId, selectedChatPenerima]
  );

  useEffect(() => {
    if (chatEndRef.current && chatMessages.length > 0) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const penerimaSelf = useMemo(
    () => instruksi?.rincian_penerima?.[0],
    [instruksi]
  );

  // Auto-simpan diterima saat halaman dibuka oleh penerima dan belum ada waktu_diterima
  const hasMarkedReceived = useRef(false);
  useEffect(() => {
    hasMarkedReceived.current = false;
  }, [instruksiId]);
  useEffect(() => {
    if (!instruksi || !isPenerima || !penerimaSelf?.id) return;
    if (penerimaSelf.waktu_diterima) return;
    if (hasMarkedReceived.current) return;
    hasMarkedReceived.current = true;
    const markAsReceived = async () => {
      try {
        await axios.put(`${API_ESA_OFFICE}instruksi/terima/${penerimaSelf.id}`);
        loadData();
      } catch (err) {
        console.error("Error updating waktu_diterima:", err);
        hasMarkedReceived.current = false;
      }
    };
    markAsReceived();
  }, [instruksi?.instruksi_id, isPenerima, penerimaSelf?.id, penerimaSelf?.waktu_diterima]);

  // Fetch penerima options untuk dialog lanjutkan
  useEffect(() => {
    if (!isPenerima || !userId) return;
    let dinas_id = null;
    let status = null;
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) {
        dinas_id = user.dinas_id;
        status = user.status;
      }
    } catch (e) {}
    if (!dinas_id) return;
    axios
      .get(
        status === "pimpinan"
          ? `${API_ESA_OFFICE}users/pimpinan/list`
          : `${API_ESA_OFFICE}users/dinas/${dinas_id}`
      )
      .then((res) => {
        const options = (res.data || [])
          .filter((d) => d.user_id !== userId)
          .map((d) => ({
            label: d.nama || "-",
            value: d.user_id,
            avatar: d.uid_RSS
              ? `https://smartdh.rotendaokab.go.id/uploads/${d.uid_RSS}.jpg`
              : undefined,
          }));
        setPenerimaOptions(options);
      })
      .catch(() => setPenerimaOptions([]));
  }, [isPenerima, userId]);

  const allOptions = useMemo(
    () => [{ label: "Pilih Semua", value: "all", avatar: "" }, ...penerimaOptions],
    [penerimaOptions]
  );

  const handlePilihTindakLanjut = (type) => {
    setOpenPilihanDialog(false);
    if (type === "selesai") {
      setOpenTindakLanjutDialog(true);
      setKeteranganTL("");
      setTanggalTL(new Date());
      setFilesTL([]);
    } else if (type === "lanjutkan") {
      setOpenModalLanjutkan(true);
      setModalValues({
        catatan: "",
        files: [],
        penerima: [],
        status_pelaksana: "Proses",
        waktu_diterima: new Date().toISOString().slice(0, 16),
        waktu_selesai: instruksi?.tanggal_selesai
          ? new Date(instruksi.tanggal_selesai).toISOString().slice(0, 16)
          : "",
      });
    }
  };

  const handleCloseTindakLanjut = () => {
    setOpenTindakLanjutDialog(false);
    setKeteranganTL("");
    setTanggalTL(new Date());
    setFilesTL([]);
    if (dropzoneRefTL.current?.dropzone) {
      dropzoneRefTL.current.dropzone.removeAllFiles();
    }
  };

  const handleCloseModalLanjutkan = () => {
    setOpenModalLanjutkan(false);
    setPenerima({ penerima: [] });
    setModalValues({
      catatan: "",
      files: [],
      penerima: [],
      status_pelaksana: "",
      waktu_diterima: "",
      waktu_selesai: "",
    });
    if (dropzoneRefLanjutkan.current?.dropzone) {
      dropzoneRefLanjutkan.current.dropzone.removeAllFiles();
    }
  };

  const handleDropzoneChangeTL = useCallback((files) => {
    const totalSizeInMB =
      (files || []).reduce((acc, file) => {
        const fileObj = file.file || file;
        return acc + (fileObj.size || 0);
      }, 0) /
      (1024 * 1024);
    if (totalSizeInMB > 1.5) {
      setFilesTL([]);
      if (dropzoneRefTL.current?.dropzone) {
        const dz = dropzoneRefTL.current.dropzone;
        dz.files.forEach((file) => dz.removeFile(file));
      }
      swal({
        title: "Ukuran File Melebihi Batas",
        text: `Total ukuran semua file (${totalSizeInMB.toFixed(2)} MB) melebihi batas maksimal 1.5 MB.`,
        icon: "error",
        button: "OK",
      });
    } else {
      setFilesTL(files || []);
    }
  }, []);

  const handleDropzoneChangeLanjutkan = useCallback((files) => {
    const totalSizeInMB =
      (files || []).reduce((acc, file) => {
        const fileObj = file.file || file;
        return acc + (fileObj.size || 0);
      }, 0) /
      (1024 * 1024);
    if (totalSizeInMB > 1.5) {
      setModalValues((prev) => ({ ...prev, files: [] }));
      if (dropzoneRefLanjutkan.current?.dropzone) {
        const dz = dropzoneRefLanjutkan.current.dropzone;
        dz.files.forEach((file) => dz.removeFile(file));
      }
      swal({
        title: "Ukuran File Melebihi Batas",
        text: `Total ukuran semua file (${totalSizeInMB.toFixed(2)} MB) melebihi batas maksimal 1.5 MB.`,
        icon: "error",
        button: "OK",
      });
    } else {
      setModalValues((prev) => ({ ...prev, files: files || [] }));
    }
  }, []);

  const dropzoneOptionsTL = useMemo(
    () => ({
      addRemoveLinks: true,
      uploadMultiple: true,
      maxTotalFilesize: 1.5,
      acceptedFiles: ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg",
      dictDefaultMessage: "Klik atau seret file ke sini untuk mengunggah",
      dictTotalFilesTooBig:
        "Total ukuran semua file melebihi batas maksimal 1.5 MB",
      dictInvalidFileType:
        "Format file tidak didukung. Hanya PDF, Word (DOC/DOCX), Excel (XLS/XLSX), dan JPG yang diperbolehkan",
      dictResponseError: "Terjadi kesalahan saat mengunggah file",
      dictCancelUpload: "Batalkan unggah",
      dictUploadCanceled: "Unggah dibatalkan",
      dictCancelUploadConfirmation:
        "Apakah Anda yakin ingin membatalkan unggah?",
      dictRemoveFile: "Hapus file",
    }),
    []
  );

  // Event handler untuk validasi file di dropzone (mengikuti daftar-penugasan)
  useEffect(() => {
    const dz = dropzoneRefTL.current?.dropzone;
    if (!dz || !openTindakLanjutDialog) return;

    const handleAccept = (file, done) => {
      if (!isValidFileType(file)) {
        done("Format file tidak didukung");
        return;
      }
      const existingSize = dz.files.reduce((sum, f) => sum + (f.size || 0), 0);
      const totalSize = existingSize + file.size;
      if (totalSize > 1.5 * 1024 * 1024) {
        done(`Total ukuran melebihi 1.5 MB`);
        swal({
          title: "Ukuran File Melebihi Batas",
          text: "Total ukuran semua file melebihi batas maksimal 1.5 MB.",
          icon: "error",
          button: "OK",
        });
        return;
      }
      done(null);
    };

    dz.on("accept", handleAccept);
    return () => {
      dz.off("accept", handleAccept);
    };
  }, [openTindakLanjutDialog]);

  // Validasi file di dropzone Dialog Lanjutkan
  useEffect(() => {
    const dz = dropzoneRefLanjutkan.current?.dropzone;
    if (!dz || !openModalLanjutkan) return;

    const handleAccept = (file, done) => {
      if (!isValidFileType(file)) {
        done("Format file tidak didukung");
        return;
      }
      const existingSize = dz.files.reduce((sum, f) => sum + (f.size || 0), 0);
      const totalSize = existingSize + file.size;
      if (totalSize > 1.5 * 1024 * 1024) {
        done(`Total ukuran melebihi 1.5 MB`);
        swal({
          title: "Ukuran File Melebihi Batas",
          text: "Total ukuran semua file melebihi batas maksimal 1.5 MB.",
          icon: "error",
          button: "OK",
        });
        return;
      }
      done(null);
    };

    dz.on("accept", handleAccept);
    return () => {
      dz.off("accept", handleAccept);
    };
  }, [openModalLanjutkan]);

  const handleSubmitSelesaikan = async () => {
    const instruksiPenerimaId = penerimaSelf?.id;
    if (!instruksiPenerimaId || !userId) {
      swal("Gagal", "Data instruksi tidak valid.", "error");
      return;
    }
    const keteranganTrimmed = (keteranganTL || "").trim();
    if (!keteranganTrimmed) {
      swal("Gagal", "Keterangan tindak lanjut wajib diisi.", "error");
      return;
    }
    if (filesTL.length > 0) {
      const totalSize = filesTL.reduce(
        (sum, f) => sum + (f.file || f).size,
        0
      );
      if (totalSize > 1.5 * 1024 * 1024) {
        swal("Gagal", "Total ukuran file melebihi 1.5 MB.", "error");
        return;
      }
    }
    try {
      setLoadingModal(true);
      const formData = new FormData();
      formData.append("catatan_selesai", keteranganTrimmed);
      formData.append("waktu_selesai", tanggalTL.toISOString().split("T")[0]);
      filesTL.forEach((f) => formData.append("file_tindak_lanjut", f.file || f));
      await axios.put(
        `${API_ESA_OFFICE}instruksi/selesaikan/${instruksiPenerimaId}/${userId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      swal("Berhasil", "Tindak lanjut berhasil disimpan", "success");
      handleCloseTindakLanjut();
      loadData();
    } catch (err) {
      swal(
        "Gagal",
        err.response?.data?.message || "Gagal menyimpan tindak lanjut",
        "error"
      );
    } finally {
      setLoadingModal(false);
    }
  };

  const handleSubmitLanjutkan = async () => {
    if (!instruksi || !userId) return;
    const penerimaList = penerima.penerima || [];
    const filtered = penerimaList.filter((p) => p.value !== "all");
    if (filtered.length === 0) {
      swal("Gagal", "Pilih minimal satu penerima.", "error");
      return;
    }
    if (!modalValues.waktu_diterima || !modalValues.waktu_selesai) {
      swal("Gagal", "Isi Waktu Diterima dan Waktu Selesai.", "error");
      return;
    }
    if (modalValues.files?.length) {
      const totalSize = modalValues.files.reduce(
        (sum, f) => sum + (f.file || f).size,
        0
      );
      if (totalSize > 1.5 * 1024 * 1024) {
        swal("Gagal", "Total ukuran file melebihi 1.5 MB.", "error");
        return;
      }
    }
    const tingkatNext = (instruksi.rincian_penerima?.[0]?.tingkat || 1) + 1;
    try {
      setLoadingModal(true);
      const formData = new FormData();
      formData.append("asal_perintah_dari_id", userId);
      formData.append("tingkat_perintah", tingkatNext);
      formData.append("instruksi_tindak_lanjut", modalValues.catatan);
      formData.append("status_pelaksana", modalValues.status_pelaksana || "Proses");
      formData.append("waktu_diterima", modalValues.waktu_diterima);
      formData.append("waktu_selesai", modalValues.waktu_selesai);
      const penerimaArr = filtered.map((p) => ({
        penerima_id: p.value,
        tingkat_perintah: tingkatNext,
        status_pelaksana: modalValues.status_pelaksana || "Proses",
        waktu_diterima: modalValues.waktu_diterima,
        waktu_selesai: modalValues.waktu_selesai,
        instruksi_tindak_lanjut: modalValues.catatan,
      }));
      formData.append("penerima", JSON.stringify(penerimaArr));
      if (modalValues.files?.length) {
        modalValues.files.forEach((f) =>
          formData.append("file_tindak_lanjut", f.file || f)
        );
      }
      await axios.post(
        `${API_ESA_OFFICE}instruksi/${instruksiId}/lanjutkan`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      swal("Berhasil", "Instruksi berhasil dilanjutkan", "success");
      handleCloseModalLanjutkan();
      loadData();
    } catch (err) {
      swal(
        "Gagal",
        err.response?.data?.error || "Gagal melanjutkan instruksi",
        "error"
      );
    } finally {
      setLoadingModal(false);
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return <Icon>insert_drive_file</Icon>;
    const ext = filename.split(".").pop()?.toLowerCase();
    if (ext === "pdf") return <PictureAsPdfIcon sx={{ color: "#f44336" }} />;
    if (["jpg", "jpeg", "png", "gif"].includes(ext))
      return <ImageIcon sx={{ color: "#4caf50" }} />;
    if (["doc", "docx"].includes(ext))
      return <DescriptionIcon sx={{ color: "#2196f3" }} />;
    return <Icon sx={{ color: "#757575" }}>insert_drive_file</Icon>;
  };

  const handleDownloadFile = (filePath) => {
    if (!filePath) return;
    if (filePath.startsWith("http")) {
      window.open(filePath, "_blank");
      return;
    }
    const base = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
    const path = String(filePath).replace(/^\//, "");
    window.open(`${base}/${path}`, "_blank");
  };

  // Handler untuk respon pemberi instruksi
  const handleOpenResponDialog = (p) => {
    setSelectedPenerimaForRespon(p);
    setSelectedRespon(p.respon_pemberi_instruksi || "");
    if (p.respon_pemberi_instruksi === "Terima" && p.nilai) {
      setSelectedRating(Number(p.nilai));
    } else {
      setSelectedRating(0);
    }
    setOpenResponDialog(true);
  };

  const handleCloseResponDialog = () => {
    setOpenResponDialog(false);
    setSelectedPenerimaForRespon(null);
    setSelectedRespon("");
    setSelectedRating(0);
  };

  const handleSelectRespon = (respon) => {
    setSelectedRespon(respon);
    if (respon === "Terima") {
      if (
        !selectedPenerimaForRespon?.respon_pemberi_instruksi ||
        selectedPenerimaForRespon?.respon_pemberi_instruksi !== "Terima" ||
        !selectedPenerimaForRespon?.nilai
      ) {
        setSelectedRating(0);
      }
      setOpenResponDialog(false);
      setOpenRatingDialog(true);
    } else {
      setSelectedRating(0);
    }
  };

  const handleSubmitRespon = async () => {
    if (!selectedPenerimaForRespon?.id) {
      swal({ title: "Error", text: "Data penerima tidak valid", icon: "error", button: "OK" });
      return;
    }
    if (!selectedRespon) {
      swal({ title: "Perhatian", text: "Silakan pilih respon terlebih dahulu", icon: "warning", button: "OK" });
      return;
    }
    if (selectedRespon === "Terima" && selectedRating === 0) {
      swal({ title: "Perhatian", text: "Silakan pilih penilaian terlebih dahulu", icon: "warning", button: "OK" });
      return;
    }
    setSubmittingRespon(true);
    try {
      await axios.put(
        `${API_ESA_OFFICE}instruksi/respon/${selectedPenerimaForRespon.id}`,
        { respon_pemberi_instruksi: selectedRespon, nilai: selectedRespon === "Terima" ? selectedRating : null }
      );
      swal({
        title: "Berhasil!",
        text:
          selectedRespon === "Terima"
            ? "Respon 'Terima' dan penilaian berhasil disimpan"
            : `Respon '${selectedRespon}' berhasil disimpan. Status diubah menjadi Proses.`,
        icon: "success",
        button: "OK",
      });
      handleCloseResponDialog();
      setOpenRatingDialog(false);
      loadData();
    } catch (err) {
      swal({
        title: "Error",
        text: err.response?.data?.message || err.response?.data?.error || "Gagal mengirimkan respon",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingRespon(false);
    }
  };

  const handleRatingClick = (rating) => setSelectedRating(rating);

  const handleSubmitRating = async () => {
    if (selectedRating === 0) {
      swal({ title: "Perhatian", text: "Silakan pilih penilaian terlebih dahulu", icon: "warning", button: "OK" });
      return;
    }
    if (!selectedPenerimaForRespon?.id) {
      swal({ title: "Error", text: "Data penerima tidak valid", icon: "error", button: "OK" });
      return;
    }
    setSubmittingRespon(true);
    try {
      await axios.put(
        `${API_ESA_OFFICE}instruksi/respon/${selectedPenerimaForRespon.id}`,
        { respon_pemberi_instruksi: "Terima", nilai: selectedRating }
      );
      swal({ title: "Berhasil!", text: "Respon 'Terima' dan penilaian berhasil disimpan", icon: "success", button: "OK" });
      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
      setSelectedPenerimaForRespon(null);
      loadData();
    } catch (err) {
      swal({
        title: "Error",
        text: err.response?.data?.message || err.response?.data?.error || "Gagal mengirimkan respon",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingRespon(false);
    }
  };

  const handleCancelRating = async () => {
    const cancelResult = await swal({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin membatalkan? Penilaian wajib diisi untuk respon 'Terima'.",
      icon: "warning",
      buttons: ["Tidak", "Ya, Batalkan"],
      dangerMode: true,
    });
    if (cancelResult) {
      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
      setSelectedPenerimaForRespon(null);
    }
  };

  const renderPenerimaCard = (penerima) => {
    const isCompleted =
      penerima.status && penerima.status.toLowerCase() === "selesai";
    const isInProgress =
      penerima.status &&
      (penerima.status.toLowerCase() === "proses" ||
        penerima.status.toLowerCase() === "on progress");

    let fileTLArr = [];
    if (penerima.file) {
      if (Array.isArray(penerima.file)) fileTLArr = penerima.file;
      else if (
        typeof penerima.file === "string" &&
        penerima.file.trim().startsWith("[")
      ) {
        try {
          fileTLArr = JSON.parse(penerima.file);
        } catch (e) {}
      } else if (typeof penerima.file === "string") {
        fileTLArr = [penerima.file];
      }
    }

    return (
      <Card
        key={penerima.id || penerima.user_id}
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {penerima.asal_perintah_nama && (
            <MDBox
              mb={2}
              p={1.5}
              sx={{
                bgcolor: "grey.50",
                borderRadius: 1.5,
                borderLeft: "4px solid",
                borderColor: "info.main",
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                <MDAvatar
                  src={
                    penerima.asal_perintah_uid_RSS
                      ? `https://smartdh.rotendaokab.go.id/uploads/${penerima.asal_perintah_uid_RSS}.jpg`
                      : undefined
                  }
                  size="sm"
                />
                <MDBox>
                  <MDTypography variant="caption" color="text.secondary">
                    Asal Perintah
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {penerima.asal_perintah_nama}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.5} color="info.main">
                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                <MDTypography variant="caption" fontWeight="medium">
                  Diteruskan ke
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          <MDBox display="flex" alignItems="center" gap={2} mb={2}>
            <MDAvatar
              src={
                penerima.uid_RSS
                  ? `https://smartdh.rotendaokab.go.id/uploads/${penerima.uid_RSS}.jpg`
                  : undefined
              }
              size="md"
              sx={{ border: "2px solid", borderColor: "grey.200" }}
            />
            <MDBox flex={1}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDTypography variant="body1" fontWeight="600">
                  {penerima.nama}
                </MDTypography>
                {/* Kirim instruksi lewat WhatsApp - hanya untuk pemberi, penerima langsung */}
                {isPemberi &&
                  penerima.asal_perintah_dari_id === userId &&
                  buildWhatsAppUrl(penerima.noHP, instruksi) && (
                  <MDBox
                    component="a"
                    href={buildWhatsAppUrl(penerima.noHP, instruksi)}
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline-flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                      color: "#25D366",
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": { color: "#128C7E" },
                    }}
                    title="Kirim instruksi via WhatsApp"
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: "inherit",
                        p: 0.25,
                        "&:hover": { bgcolor: "rgba(37,211,102,0.08)" },
                      }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                    <MDTypography variant="caption" color="inherit" sx={{ fontWeight: 500 }}>
                      kirim instruksi lewat WhatsApp
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              {penerima.tingkat && (
                <MDTypography variant="caption" color="text.secondary">
                  Level {penerima.tingkat}
                </MDTypography>
              )}
            </MDBox>
            {isCompleted ? (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                label="Selesai"
                color="success"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            ) : isInProgress ? (
              <Chip
                icon={<HourglassBottomIcon sx={{ fontSize: 18 }} />}
                label="Proses"
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <Chip
                icon={<CancelIcon sx={{ fontSize: 18 }} />}
                label="Belum"
                color="error"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </MDBox>

          <Grid container spacing={2} sx={{ mb: penerima.catatan_selesai || fileTLArr.length > 0 ? 2 : 0 }}>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <CalendarTodayIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <MDBox>
                  <MDTypography variant="caption" color="text.secondary" display="block">
                    Diterima
                  </MDTypography>
                  <MDTypography variant="body2">
                    {penerima.waktu_diterima
                      ? formatDateTime(penerima.waktu_diterima)
                      : "Belum diterima"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            {isCompleted && penerima.waktu_diselesaikan && (
              <Grid item xs={12} sm={6}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                  <MDBox>
                    <MDTypography variant="caption" color="text.secondary" display="block">
                      Diselesaikan
                    </MDTypography>
                    <MDTypography variant="body2">
                      {formatDateTime(penerima.waktu_diselesaikan)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Grid>
            )}
          </Grid>

          {isCompleted && penerima.catatan_selesai && (
            <MDBox
              mt={1.5}
              p={2}
              sx={{
                background: "linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%)",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "grey.300",
                "& a": {
                  color: "primary.main",
                  textDecoration: "underline",
                  "&:hover": { textDecoration: "underline" },
                },
              }}
            >
              <MDTypography variant="caption" fontWeight="700" color="info.main" display="block" mb={0.5}>
                Catatan Selesai
              </MDTypography>
              <MDTypography
                variant="body2"
                component="div"
                sx={{ lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{
                  __html: processHtmlWithClickableLinks(penerima.catatan_selesai),
                }}
              />
            </MDBox>
          )}

          {fileTLArr.length > 0 && (
            <MDBox mt={1.5}>
              <MDTypography variant="caption" fontWeight="600" color="text.secondary" display="block" mb={1}>
                File Tindak Lanjut
              </MDTypography>
              <MDBox display="flex" flexWrap="wrap" gap={1}>
                {fileTLArr.map((file, idx) => {
                  const filename = file.split("/").pop();
                  return (
                    <Button
                      key={idx}
                      size="small"
                      variant="outlined"
                      startIcon={getFileIcon(filename)}
                      onClick={() => handleDownloadFile(file)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 1.5,
                        borderColor: "grey.300",
                        "&:hover": { borderColor: "info.main", bgcolor: "rgba(38,38,38,0.04)" },
                      }}
                    >
                      {filename}
                    </Button>
                  );
                })}
              </MDBox>
            </MDBox>
          )}

          {/* Respon Pemberi Instruksi - hanya tampil jika pemberi, status Selesai, waktu_diselesaikan, dan penerima langsung */}
          {isPemberi &&
            isCompleted &&
            penerima.waktu_diselesaikan &&
            penerima.asal_perintah_dari_id === userId && (
              <MDBox mt={2} pt={1.5} sx={{ borderTop: 1, borderColor: "divider" }}>
                {penerima.respon_pemberi_instruksi && (
                  <MDBox
                    sx={{
                      p: 1.5,
                      background: "#f0f7ff",
                      borderRadius: 1,
                      border: "1px solid #b3d9ff",
                      mb: 2,
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                          Respon Anda:
                        </MDTypography>
                        <Chip
                          label={penerima.respon_pemberi_instruksi}
                          size="small"
                          color={
                            penerima.respon_pemberi_instruksi === "Terima"
                              ? "success"
                              : penerima.respon_pemberi_instruksi === "Tolak"
                              ? "error"
                              : "warning"
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </MDBox>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenResponDialog(penerima)}
                        sx={{
                          p: 0.5,
                          "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                        }}
                        title="Edit Respon"
                      >
                        <Icon sx={{ fontSize: "18px" }}>edit</Icon>
                      </IconButton>
                    </MDBox>
                    {penerima.respon_pemberi_instruksi === "Terima" && penerima.nilai && (
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                          Penilaian:
                        </MDTypography>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Icon
                            key={rating}
                            sx={{
                              fontSize: "20px",
                              color: penerima.nilai >= rating ? "#FFD700" : "#E0E0E0",
                            }}
                          >
                            star
                          </Icon>
                        ))}
                        <MDTypography variant="caption" color="text.secondary" ml={0.5}>
                          ({penerima.nilai} bintang)
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )}
                {(!penerima.respon_pemberi_instruksi ||
                  penerima.respon_pemberi_instruksi === "Perbaiki" ||
                  penerima.respon_pemberi_instruksi === "Tolak") && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={() => handleOpenResponDialog(penerima)}
                    startIcon={<Icon>rate_review</Icon>}
                    fullWidth
                  >
                    {penerima.respon_pemberi_instruksi ? "Ubah Respon" : "Berikan Respon"}
                  </MDButton>
                )}
              </MDBox>
            )}
          {/* Tampilkan respon saja jika bukan pemberi (penerima melihat) */}
          {!isPemberi && penerima.respon_pemberi_instruksi && (
            <MDBox mt={1.5} pt={1.5} sx={{ borderTop: 1, borderColor: "divider" }}>
              <MDTypography variant="caption" color="text.secondary">
                Respon: <strong>{penerima.respon_pemberi_instruksi}</strong>
              </MDTypography>
              {penerima.nilai != null &&
                penerima.nilai !== "" &&
                Number(penerima.nilai) > 0 && (
                  <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon
                        key={star}
                        sx={{
                          color: star <= Number(penerima.nilai) ? "#FFD700" : "#E0E0E0",
                          fontSize: "1.2rem",
                        }}
                      >
                        star
                      </Icon>
                    ))}
                    <MDTypography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({penerima.nilai}/5)
                    </MDTypography>
                  </MDBox>
                )}
            </MDBox>
          )}

          {/* Chat - gabung di card penerima */}
          {getChatPartnerForPenerima(penerima) && (
            <MDBox
              mt={2}
              pt={1.5}
              sx={{
                borderTop: 1,
                borderColor: "divider",
                bgcolor: "grey.50",
                borderRadius: 1.5,
                p: 1.5,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <ChatIcon sx={{ color: "info.main", fontSize: 20 }} />
                  <MDTypography variant="button" fontWeight="600" color="text">
                    Chat dengan {isPenerima ? "Pengirim" : penerima.nama}
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => handleOpenChat(penerima)}
                  startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
                >
                  Buka Chat
                </MDButton>
              </MDBox>
              {(() => {
                const messages = chatMessagesByPenerima[penerima.id] || [];
                const user = JSON.parse(localStorage.getItem("user")) || {};
                const baseIds = [userId, user.user_id, user.uid, user.uid_RSS];
                if (isPemberi && instruksi?.pengirim_id) baseIds.push(instruksi.pengirim_id);
                if (isPenerima && penerima?.penerima_id) baseIds.push(penerima.penerima_id);
                const myIds = [...new Set(baseIds.filter(Boolean).map(String))];
                const unread = messages.filter((m) => myIds.includes(String(m.penerima_id)) && !m.waktu_baca).length;
                return (
                  <>
                    {unread > 0 && (
                      <MDBox
                        component="button"
                        type="button"
                        onClick={() => handleOpenChat(penerima)}
                        mb={1}
                        px={1.5}
                        py={0.5}
                        sx={{
                          width: "100%",
                          textAlign: "left",
                          cursor: "pointer",
                          bgcolor: "error.lighter",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "error.main",
                          "&:hover": {
                            bgcolor: "error.main",
                            "& .MuiTypography-root": { color: "white" },
                          },
                        }}
                      >
                        <MDTypography variant="caption" color="error" fontWeight="600">
                          {unread} pesan belum dibaca - Klik untuk buka chat
                        </MDTypography>
                      </MDBox>
                    )}
                    <MDBox
                      sx={{
                        maxHeight: 140,
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.2)", borderRadius: 3 },
                      }}
                    >
                      {messages.length === 0 ? (
                        <MDTypography variant="caption" color="text.secondary">
                          Belum ada percakapan
                        </MDTypography>
                      ) : (
                        messages.map((msg, idx) => {
                          const isOwn = myIds.includes(String(msg.pengirim_id));
                          return (
                            <MDBox
                              key={msg.id_chat || idx}
                              display="flex"
                              alignItems="flex-end"
                              justifyContent={isOwn ? "flex-end" : "flex-start"}
                              gap={0.5}
                              sx={{ mb: 1, width: "100%" }}
                            >
                              {!isOwn ? (
                                <>
                                  <MDAvatar
                                    src={
                                      msg.pengirim_profil
                                        ? `https://smartdh.rotendaokab.go.id/uploads/${msg.pengirim_profil}.jpg`
                                        : undefined
                                    }
                                    size="xs"
                                    sx={{ flexShrink: 0, alignSelf: "flex-end", mb: 0.25 }}
                                  />
                                  <MDBox
                                    sx={{
                                      maxWidth: "85%",
                                      p: 1,
                                      borderRadius: "12px 12px 12px 4px",
                                      bgcolor: "#E9EDEF",
                                      border: "1px solid",
                                      borderColor: "grey.200",
                                    }}
                                  >
                                    <MDTypography variant="caption" fontWeight="600" color="text.secondary" display="block" sx={{ fontSize: "0.7rem" }}>
                                      {msg.pengirim_nama || "-"}
                                    </MDTypography>
                                    <MDTypography variant="body2" sx={{ wordBreak: "break-word", fontSize: "0.85rem" }}>
                                      {msg.pesan}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                                      {formatChatTime(msg.waktu_kirim)}
                                    </MDTypography>
                                  </MDBox>
                                </>
                              ) : (
                                <>
                                  <MDBox
                                    sx={{
                                      maxWidth: "85%",
                                      p: 1,
                                      borderRadius: "12px 12px 4px 12px",
                                      bgcolor: "#D9FDD3",
                                      border: "1px solid",
                                      borderColor: "rgba(0,92,75,0.2)",
                                    }}
                                  >
                                    <MDTypography variant="body2" sx={{ wordBreak: "break-word", fontSize: "0.85rem" }}>
                                      {msg.pesan}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                                      {formatChatTime(msg.waktu_kirim)}
                                    </MDTypography>
                                  </MDBox>
                                  <MDAvatar
                                    src={
                                      user.uid_RSS
                                        ? `https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`
                                        : undefined
                                    }
                                    size="xs"
                                    sx={{ flexShrink: 0, alignSelf: "flex-end", mb: 0.25 }}
                                  />
                                </>
                              )}
                            </MDBox>
                          );
                        })
                      )}
                    </MDBox>
                  </>
                );
              })()}
            </MDBox>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          gap={2}
        >
          <CircularProgress size={48} thickness={4} />
          <MDTypography variant="body2" color="text.secondary">
            Memuat detail instruksi...
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!instruksi && !loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <Card sx={{ p: 4, textAlign: "center" }}>
            {unauthorized ? (
              <>
                <Icon sx={{ fontSize: 64, color: "error.main", mb: 2 }}>
                  lock
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" mb={1}>
                  Akses Ditolak
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={3}>
                  Anda tidak memiliki akses untuk melihat instruksi ini. Halaman ini hanya dapat dibuka oleh pemberi instruksi dan penerima yang terkait.
                </MDTypography>
              </>
            ) : (
              <>
                <AssignmentIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                <MDTypography variant="h5" fontWeight="bold" mb={1}>
                  Instruksi tidak ditemukan
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={3}>
                  Data instruksi yang Anda cari tidak tersedia atau telah dihapus.
                </MDTypography>
              </>
            )}
            <Button
              variant="contained"
              color="info"
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                navigate(
                  unauthorized ? -1 : "/task-management/tugas/daftar-instruksi"
                )
              }
              sx={{ borderRadius: 2 }}
            >
              {unauthorized ? "Kembali" : "Kembali ke Daftar Instruksi"}
            </Button>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  let dokumenArr = [];
  if (instruksi.dokumen) {
    if (Array.isArray(instruksi.dokumen)) dokumenArr = instruksi.dokumen;
    else if (
      typeof instruksi.dokumen === "string" &&
      instruksi.dokumen.trim().startsWith("[")
    ) {
      try {
        dokumenArr = JSON.parse(instruksi.dokumen);
      } catch (e) {}
    }
  }

  const penerimaLangsung = (instruksi.rincian_penerima || []).filter(
    (p) => p.asal_perintah_dari_id === userId
  );
  const penerimaLanjutan = (instruksi.rincian_penerima || []).filter(
    (p) => p.asal_perintah_dari_id !== userId
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={3}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  <IconButton
                    color="white"
                    onClick={() =>
                      navigate(
                        isPemberi
                          ? "/task-management/tugas/daftar-instruksi"
                          : "/task-management/tugas/daftar-tugas"
                      )
                    }
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <MDBox>
                    <MDTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                      Detail Instruksi
                    </MDTypography>
                    <MDTypography variant="h5" color="white" fontWeight="bold">
                      {instruksi.perihal || "-"}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Chip
                    label={instruksi.prioritas || "-"}
                    size="medium"
                    sx={{
                      backgroundColor: PRIORITAS_COLOR[instruksi.prioritas] || "#757575",
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "white",
                      opacity: 0.95,
                    }}
                  >
                    <CalendarTodayIcon sx={{ fontSize: 18, color: "#ffffff !important" }} />
                    <MDTypography variant="body2" color="white">
                      {formatDate(instruksi.tanggal)}
                      {instruksi.tanggal_selesai && ` • Deadline: ${formatDate(instruksi.tanggal_selesai)}`}
                    </MDTypography>
                  </Box>
                </MDBox>
              </MDBox>

              <MDBox pt={4} pb={3} px={3}>
                <Grid container spacing={4}>
                  <Grid item xs={12} lg={8}>
                    <MDBox
                      sx={{
                        p: 3,
                        background: "linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%)",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                        <AssignmentIcon sx={{ color: "info.main", fontSize: 24 }} />
                        <MDTypography variant="h6" fontWeight="bold" color="text">
                          Isi Instruksi
                        </MDTypography>
                      </MDBox>
                      {instruksi.isi_instruksi ? (
                        <Box
                          sx={{
                            "& p": { marginBottom: 1.5 },
                            "& ul, & ol": { pl: 2.5, mb: 1.5 },
                            "& a": {
                              color: "primary.main",
                              textDecoration: "underline",
                              "&:hover": { textDecoration: "underline" },
                            },
                            fontSize: "0.9375rem",
                            color: "text.primary",
                            lineHeight: 1.8,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: processHtmlWithClickableLinks(instruksi.isi_instruksi),
                          }}
                        />
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          -
                        </MDTypography>
                      )}
                    </MDBox>

                    {dokumenArr.length > 0 && (
                      <MDBox mt={3}>
                        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                          <InsertDriveFileIcon sx={{ color: "info.main", fontSize: 22 }} />
                          <MDTypography variant="h6" fontWeight="bold">
                            Dokumen Lampiran
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" flexWrap="wrap" gap={1}>
                          {dokumenArr.map((file, idx) => {
                            const filename = file.split("/").pop();
                            return (
                              <Button
                                key={idx}
                                size="medium"
                                variant="outlined"
                                startIcon={getFileIcon(filename)}
                                onClick={() => handleDownloadFile(file)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 2,
                                  borderColor: "grey.300",
                                  "&:hover": {
                                    borderColor: "info.main",
                                    bgcolor: "grey.100",
                                  },
                                }}
                              >
                                {filename}
                              </Button>
                            );
                          })}
                        </MDBox>
                      </MDBox>
                    )}
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                          <PersonIcon sx={{ color: "info.main", fontSize: 22 }} />
                          <MDTypography variant="h6" fontWeight="bold">
                            Pengirim
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={2}
                          p={2}
                          sx={{
                            bgcolor: "grey.50",
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <MDAvatar
                            src={
                              instruksi.pengirim?.uid_RSS
                                ? `https://smartdh.rotendaokab.go.id/uploads/${instruksi.pengirim.uid_RSS}.jpg`
                                : undefined
                            }
                            size="lg"
                            sx={{ border: "3px solid white", boxShadow: 2 }}
                          />
                          <MDTypography variant="body1" fontWeight="600">
                            {instruksi.pengirim?.nama || "-"}
                          </MDTypography>
                        </MDBox>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <MDBox>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={2}
                    mb={3}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <GroupsIcon sx={{ color: "info.main", fontSize: 24 }} />
                      <MDTypography variant="h6" fontWeight="bold">
                        {isPenerima
                          ? "Informasi Penerima (Anda)"
                          : "Penerima Instruksi"}
                      </MDTypography>
                    </MDBox>
                    {isPenerima &&
                      instruksi.rincian_penerima?.[0] &&
                      instruksi.rincian_penerima[0].status?.toLowerCase() !== "selesai" && (
                      <Button
                        variant="contained"
                        color="info"
                        size="medium"
                        startIcon={<SendIcon />}
                        onClick={() => setOpenPilihanDialog(true)}
                      >
                        Tindak Lanjut
                      </Button>
                    )}
                  </MDBox>

                  {isPenerima ? (
                    instruksi.rincian_penerima?.length > 0 &&
                    instruksi.rincian_penerima.map((p) => renderPenerimaCard(p))
                  ) : (
                    <>
                      {penerimaLangsung.length > 0 && (
                        <MDBox mb={3}>
                          <MDBox
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            px={2}
                            py={0.75}
                            mb={2}
                            sx={{
                              bgcolor: "grey.100",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "grey.300",
                            }}
                          >
                            <MDTypography variant="button" color="info" fontWeight="bold">
                              Penerima Langsung ({penerimaLangsung.length})
                            </MDTypography>
                          </MDBox>
                          {penerimaLangsung.map((p) => renderPenerimaCard(p))}
                        </MDBox>
                      )}

                      {penerimaLanjutan.length > 0 && (
                        <MDBox>
                          <MDBox
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            px={2}
                            py={0.75}
                            mb={2}
                            sx={{
                              bgcolor: "grey.100",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "grey.300",
                            }}
                          >
                            <MDTypography variant="button" color="secondary" fontWeight="bold">
                              Penerima Lanjutan ({penerimaLanjutan.length})
                            </MDTypography>
                          </MDBox>
                          {penerimaLanjutan.map((p) => renderPenerimaCard(p))}
                        </MDBox>
                      )}
                    </>
                  )}

                  {!isPenerima &&
                    (!instruksi.rincian_penerima ||
                      instruksi.rincian_penerima.length === 0) && (
                      <MDBox
                        py={4}
                      textAlign="center"
                      sx={{
                        bgcolor: "grey.50",
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "grey.300",
                      }}
                    >
                      <GroupsIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                      <MDTypography variant="body2" color="text.secondary">
                        Tidak ada penerima
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Pilihan Tindak Lanjut */}
      <Dialog
        open={openPilihanDialog}
        onClose={() => setOpenPilihanDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center">
            <SendIcon sx={{ mr: 1 }} />
            Pilih Tindak Lanjut
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={isMobile ? 2 : 3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6" mb={2}>
                  Detail Instruksi
                </MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>Pengirim:</strong> {instruksi?.pengirim?.nama || "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Tanggal:</strong>{" "}
                    {instruksi?.tanggal ? formatDate(instruksi.tanggal) : "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Perihal:</strong> {instruksi?.perihal || "-"}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="h6" mb={2}>
                  Pilih Tindak Lanjut
                </MDTypography>
                <MDBox display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => handlePilihTindakLanjut("selesai")}
                    startIcon={<CheckCircleIcon />}
                  >
                    Selesaikan Instruksi
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    fullWidth
                    onClick={() => handlePilihTindakLanjut("lanjutkan")}
                    startIcon={<SendIcon />}
                  >
                    Lanjutkan ke Penerima Lain
                  </Button>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPilihanDialog(false)}>Batal</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Selesaikan Instruksi */}
      <Dialog
        open={openTindakLanjutDialog}
        onClose={handleCloseTindakLanjut}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Selesaikan Instruksi</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={isMobile ? 2 : 3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6">Detail Instruksi</MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>Pengirim:</strong> {instruksi?.pengirim?.nama || "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Tanggal:</strong>{" "}
                    {instruksi?.tanggal ? formatDate(instruksi.tanggal) : "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Perihal:</strong> {instruksi?.perihal || "-"}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDDatePicker
                  input={{
                    label: "Tanggal Tindak Lanjut",
                    variant: "standard",
                    fullWidth: true,
                  }}
                  value={tanggalTL}
                  onChange={(date) => setTanggalTL(date)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Keterangan Tindak Lanjut"
                  multiline
                  rows={4}
                  fullWidth
                  value={keteranganTL}
                  onChange={(e) => setKeteranganTL(e.target.value)}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mt={2}>
                  <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <MDTypography
                      component="label"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      Upload dokumen, jika ada (bisa lebih dari satu file)
                      <br />
                      <MDTypography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        Format: PDF, Word, Excel, JPG | Maksimal total: 1.5 MB
                        untuk semua file
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                  <MDDropzone
                    options={dropzoneOptionsTL}
                    onChange={handleDropzoneChangeTL}
                    ref={dropzoneRefTL}
                  />
                  <MDBox mt={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      * Format: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG
                      <br />* Maksimal total ukuran: 1.5 MB
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTindakLanjut}>Batal</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmitSelesaikan}
            disabled={
              loadingModal ||
              filesTL.reduce((s, f) => s + (f.file || f).size || 0, 0) >
                1.5 * 1024 * 1024
            }
          >
            {loadingModal ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Lanjutkan ke Penerima Lain */}
      <Dialog
        open={openModalLanjutkan}
        onClose={handleCloseModalLanjutkan}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Lanjutkan Instruksi ke Level Bawah</DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <TextField
            label="Catatan/Instruksi"
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            value={modalValues.catatan}
            onChange={(e) =>
              setModalValues((prev) => ({ ...prev, catatan: e.target.value }))
            }
          />
          <MDBox mt={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" mb={1}>
              Penerima Instruksi
            </MDTypography>
            <Autocomplete
              multiple
              options={allOptions}
              value={penerima.penerima || []}
              onChange={(e, value) => {
                const hasAll = value.some((v) => v.value === "all");
                if (hasAll) {
                  const currentLen = (penerima.penerima || []).length;
                  setPenerima({
                    penerima:
                      currentLen === penerimaOptions.length ? [] : penerimaOptions,
                  });
                } else {
                  setPenerima({ penerima: value });
                }
              }}
              getOptionLabel={(option) => option.label}
              renderOption={(props, option) => (
                <li {...props} style={{ display: "flex", alignItems: "center" }}>
                  {option.value === "all" ? (
                    <strong style={{ color: "#1976d2" }}>Pilih Semua</strong>
                  ) : (
                    <>
                      <MDAvatar
                        src={option.avatar}
                        alt={option.label}
                        size="xs"
                        sx={{ mr: 1 }}
                      />
                      {option.label}
                    </>
                  )}
                </li>
              )}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  label="Pilih Penerima"
                />
              )}
            />
          </MDBox>
          <TextField
            label="Waktu Diterima"
            type="datetime-local"
            fullWidth
            sx={{ mt: 2 }}
            value={modalValues.waktu_diterima}
            onChange={(e) =>
              setModalValues((prev) => ({
                ...prev,
                waktu_diterima: e.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Waktu Selesai"
            type="datetime-local"
            fullWidth
            sx={{ mt: 2 }}
            value={modalValues.waktu_selesai}
            onChange={(e) =>
              setModalValues((prev) => ({
                ...prev,
                waktu_selesai: e.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <MDBox mt={2}>
            <MDTypography variant="button" fontWeight="regular" color="text" mb={1}>
              Upload dokumen (opsional, max 1.5 MB total)
            </MDTypography>
            <MDDropzone
              options={dropzoneOptionsTL}
              onChange={handleDropzoneChangeLanjutkan}
              ref={dropzoneRefLanjutkan}
            />
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text.secondary">
                * Format: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG
                <br />* Maksimal total ukuran: 1.5 MB
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModalLanjutkan}>Batal</Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleSubmitLanjutkan}
            disabled={loadingModal}
          >
            {loadingModal ? "Mengirim..." : "Kirim"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Respon Pemberi Instruksi */}
      <Dialog
        open={openResponDialog}
        onClose={handleCloseResponDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            {selectedPenerimaForRespon?.respon_pemberi_instruksi ? "Edit Respon" : "Berikan Respon"}
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          {selectedPenerimaForRespon && (
            <MDBox py={2}>
              <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  src={
                    selectedPenerimaForRespon.uid_RSS
                      ? `https://smartdh.rotendaokab.go.id/uploads/${selectedPenerimaForRespon.uid_RSS}.jpg`
                      : undefined
                  }
                  alt={selectedPenerimaForRespon.nama}
                  sx={{
                    width: "100px",
                    height: "100px",
                    border: "3px solid",
                    borderColor: "primary.main",
                    mb: 2,
                    boxShadow: 3,
                    fontSize: "2.5rem",
                    bgcolor: "info",
                  }}
                >
                  {selectedPenerimaForRespon.nama
                    ? selectedPenerimaForRespon.nama.charAt(0).toUpperCase()
                    : "👤"}
                </Avatar>
                <MDTypography variant="h6" color="text" fontWeight="medium">
                  {selectedPenerimaForRespon.nama || "Penerima"}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Penerima Instruksi
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" mb={2} textAlign="center">
                Pilih respon Anda terhadap penyelesaian instruksi ini:
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={1.5}>
                <MDButton
                  variant={selectedRespon === "Terima" ? "gradient" : "outlined"}
                  color="success"
                  onClick={() => handleSelectRespon("Terima")}
                  startIcon={<Icon>check_circle</Icon>}
                  fullWidth
                  sx={{ justifyContent: "flex-start", py: 1.5 }}
                >
                  Terima
                </MDButton>
                <MDButton
                  variant={selectedRespon === "Perbaiki" ? "gradient" : "outlined"}
                  color="warning"
                  onClick={() => setSelectedRespon("Perbaiki")}
                  startIcon={<Icon>edit</Icon>}
                  fullWidth
                  sx={{ justifyContent: "flex-start", py: 1.5 }}
                >
                  Perbaiki
                </MDButton>
                <MDButton
                  variant={selectedRespon === "Tolak" ? "gradient" : "outlined"}
                  color="error"
                  onClick={() => setSelectedRespon("Tolak")}
                  startIcon={<Icon>cancel</Icon>}
                  fullWidth
                  sx={{ justifyContent: "flex-start", py: 1.5 }}
                >
                  Tolak
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleCloseResponDialog}>
            Batal
          </MDButton>
          {selectedRespon !== "Terima" && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleSubmitRespon}
              disabled={!selectedRespon || submittingRespon}
              startIcon={<Icon>send</Icon>}
            >
              {submittingRespon ? "Mengirim..." : "Kirim"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Rating Bintang untuk Respon Terima */}
      <Dialog
        open={openRatingDialog}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown
      >
        <DialogTitle>
          <MDTypography variant="h5" color="text" fontWeight="bold">
            {selectedPenerimaForRespon?.respon_pemberi_instruksi === "Terima" &&
            selectedPenerimaForRespon?.nilai
              ? "Edit Penilaian Instruksi"
              : "Penilaian Instruksi"}
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <MDBox textAlign="center" py={2}>
            {selectedPenerimaForRespon && (
              <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  src={
                    selectedPenerimaForRespon.uid_RSS
                      ? `https://smartdh.rotendaokab.go.id/uploads/${selectedPenerimaForRespon.uid_RSS}.jpg`
                      : undefined
                  }
                  alt={selectedPenerimaForRespon.nama}
                  sx={{
                    width: "100px",
                    height: "100px",
                    border: "3px solid",
                    borderColor: "primary.main",
                    mb: 2,
                    boxShadow: 3,
                    fontSize: "2.5rem",
                    bgcolor: "info",
                  }}
                >
                  {selectedPenerimaForRespon.nama
                    ? selectedPenerimaForRespon.nama.charAt(0).toUpperCase()
                    : "👤"}
                </Avatar>
                <MDTypography variant="h6" color="text" fontWeight="medium">
                  {selectedPenerimaForRespon.nama || "Penerima"}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Penerima Instruksi
                </MDTypography>
              </MDBox>
            )}
            <MDTypography variant="body2" color="text" mb={2}>
              Silakan berikan penilaian Anda terhadap penyelesaian instruksi:
            </MDTypography>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{ py: 3 }}
            >
              {[1, 2, 3, 4, 5].map((rating) => (
                <MDBox
                  key={rating}
                  onClick={() => handleRatingClick(rating)}
                  sx={{
                    cursor: "pointer",
                    width: "75px",
                    height: "75px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { transform: "scale(1.1)", transition: "transform 0.2s" },
                    transition: "transform 0.2s",
                    "@media (max-width: 600px)": { width: "50px", height: "50px" },
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="60"
                    height="60"
                    fill={selectedRating >= rating ? "#FFD700" : "#E0E0E0"}
                    style={{ transition: "fill 0.2s" }}
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </MDBox>
              ))}
            </MDBox>
            {selectedRating > 0 && (
              <MDTypography variant="body2" color="text.secondary" mt={2} sx={{ fontStyle: "italic" }}>
                Anda memberikan {selectedRating} bintang
                {selectedRating === 1
                  ? " (Sangat Tidak Puas)"
                  : selectedRating === 2
                  ? " (Tidak Puas)"
                  : selectedRating === 3
                  ? " (Cukup)"
                  : selectedRating === 4
                  ? " (Puas)"
                  : " (Sangat Puas)"}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleCancelRating}>
            Batal
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleSubmitRating}
            disabled={selectedRating === 0 || submittingRespon}
            startIcon={<Icon>send</Icon>}
          >
            {submittingRespon ? "Mengirim..." : "Kirim"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Chat Instruksi */}
      <Dialog
        open={openChatDialog}
        onClose={handleCloseChat}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? "100%" : "70vh",
            maxHeight: "90vh",
            borderRadius: isMobile ? 0 : 2,
            overflow: "hidden",
          },
        }}
      >
        <MDBox
          sx={{
            bgcolor: "info.main",
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDAvatar
              src={
                selectedChatPenerima?.uid_RSS
                  ? `https://smartdh.rotendaokab.go.id/uploads/${selectedChatPenerima.uid_RSS}.jpg`
                  : undefined
              }
              alt={selectedChatPenerima?.nama}
              size="sm"
              sx={{ border: "2px solid rgba(255,255,255,0.3)" }}
            />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                {selectedChatPenerima?.nama || "-"}
              </MDTypography>
              <MDTypography variant="caption" color="white">
                {instruksi?.perihal || "Instruksi"}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <IconButton
              onClick={handleRefreshChat}
              disabled={refreshingChat || loadingChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: refreshingChat ? "grey.500" : "grey.700",
                "&:hover": { bgcolor: "grey.100" },
                "&:disabled": { bgcolor: "grey.300", color: "grey.500" },
              }}
              title="Refresh Chat"
            >
              {refreshingChat ? (
                <CircularProgress size={20} sx={{ color: "grey.500" }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <IconButton
              onClick={handleCloseChat}
              size="small"
              sx={{ bgcolor: "white", color: "grey.700", "&:hover": { bgcolor: "grey.100" } }}
            >
              <CancelIcon />
            </IconButton>
          </MDBox>
        </MDBox>

        <MDBox sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "grey.100" }}>
          {loadingChat ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.2)", borderRadius: "4px" },
              }}
            >
              {chatMessages.length === 0 ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" flex={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada pesan. Mulai percakapan!
                  </MDTypography>
                </MDBox>
              ) : (
                chatMessages.map((message, idx) => {
                  const user = JSON.parse(localStorage.getItem("user")) || {};
                  // pengirim_id dari API = user_id. Cek semua kemungkinan ID (uid/user_id/uid_RSS)
                  const myIds = [userId, user.user_id, user.uid, user.uid_RSS].filter(Boolean).map(String);
                  const isOwnMessage = myIds.includes(String(message.pengirim_id));
                  const isSending = message.isSending;
                  return (
                    <MDBox
                      key={message.id_chat || idx}
                      display="flex"
                      justifyContent={isOwnMessage ? "flex-end" : "flex-start"}
                      sx={{
                        mb: 1.5,
                        width: "100%",
                        px: 0.5,
                      }}
                    >
                      {/* Pesan lawan - KIRI ( seperti WhatsApp ) */}
                      {!isOwnMessage && (
                        <MDBox
                          display="flex"
                          alignItems="flex-end"
                          gap={0.75}
                          sx={{ maxWidth: "85%", flexShrink: 0 }}
                        >
                          <MDAvatar
                            src={
                              message.pengirim_profil
                                ? `https://smartdh.rotendaokab.go.id/uploads/${message.pengirim_profil}.jpg`
                                : undefined
                            }
                            size="sm"
                            sx={{ flexShrink: 0, alignSelf: "flex-end", mb: 0.5 }}
                          />
                          <MDBox
                            sx={{
                              p: 1.25,
                              px: 1.5,
                              borderRadius: "18px 18px 18px 4px",
                              backgroundColor: "#E9EDEF",
                              color: "text.primary",
                              wordBreak: "break-word",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                              border: "1px solid",
                              borderColor: "grey.200",
                              maxWidth: "100%",
                            }}
                          >
                            <MDTypography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.7rem", mb: 0.25 }}>
                              {message.pengirim_nama || "-"}
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              sx={{ fontSize: "0.95rem", whiteSpace: "pre-wrap", color: "text.primary" }}
                            >
                              {message.pesan}
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary", display: "block", textAlign: "right", mt: 0.5 }}>
                              {formatChatTime(message.waktu_kirim)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      )}
                      {/* Pesan sendiri - KANAN ( struktur seperti daftar-tugas ) */}
                      {isOwnMessage && (
                        <MDBox
                          sx={{
                            maxWidth: "75%",
                            minWidth: "100px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            marginLeft: "auto",
                          }}
                        >
                          <MDBox display="flex" alignItems="flex-end" gap={0.75}>
                            <MDBox
                              sx={{
                                p: 1.25,
                                px: 1.5,
                                borderRadius: "18px 18px 4px 18px",
                                backgroundColor: "#D9FDD3",
                                color: "text.primary",
                                wordBreak: "break-word",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                                border: "1px solid",
                                borderColor: "rgba(0,92,75,0.2)",
                                opacity: isSending ? 0.8 : 1,
                                maxWidth: "100%",
                              }}
                            >
                              <MDTypography
                                variant="body2"
                                sx={{ fontSize: "0.95rem", whiteSpace: "pre-wrap", color: "text.primary" }}
                              >
                                {message.pesan}
                                {isSending && (
                                  <CircularProgress size={12} sx={{ color: "grey.600", ml: 0.5, display: "inline-block", verticalAlign: "middle" }} />
                                )}
                              </MDTypography>
                              {/* Timestamp, status dibaca, dan delete - di dalam bubble */}
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={0.5}
                                sx={{ mt: 0.75, pt: 0.5 }}
                              >
                                <MDTypography variant="caption" sx={{ fontSize: "0.75rem", color: "#2e7d32" }}>
                                  {formatChatTime(message.waktu_kirim)}
                                </MDTypography>
                                {!isSending && (
                                  <CheckCircleIcon sx={{ fontSize: 14, color: message.waktu_baca ? "#2e7d32" : "grey.500" }} />
                                )}
                                {!isSending && message.waktu_baca && (
                                  <MDTypography variant="caption" sx={{ fontSize: "0.7rem", color: "#2e7d32", fontStyle: "italic" }}>
                                    Dibaca {formatChatTime(message.waktu_baca)}
                                  </MDTypography>
                                )}
                                {!isSending && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteMessage(message.id_chat)}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      color: "#2e7d32",
                                      "&:hover": { color: "error.main", bgcolor: "rgba(211,47,47,0.15)" },
                                    }}
                                    title="Hapus pesan"
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                              </MDBox>
                            </MDBox>
                            <MDAvatar
                              src={
                                user.uid_RSS
                                  ? `https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`
                                  : undefined
                              }
                              size="sm"
                              sx={{ flexShrink: 0, alignSelf: "flex-end" }}
                            />
                          </MDBox>
                        </MDBox>
                      )}
                    </MDBox>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </MDBox>
          )}

          <MDBox
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "white",
            }}
          >
            <MDBox display="flex" gap={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ketik pesanmu..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "grey.50",
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || loadingChat}
                sx={{
                  bgcolor: "info.main",
                  color: "white",
                  "&:hover": { bgcolor: "info.dark" },
                  "&:disabled": { bgcolor: "grey.300", color: "grey.500" },
                }}
              >
                <SendIcon />
              </IconButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default InstruksiDetail;
